import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import {
  LOOPS_NEWSLETTER_FORM_ENDPOINT,
  LOOPS_DEFAULT_USER_GROUP,
} from "../constants/loops";
import { trackEvent } from "../utils/posthog";

// --- suppression (localStorage + sessionStorage) -----------------------------
// Rules (agreed spec):
//   - At most once per browser session.
//   - At most once per 24h per visitor.
//   - Dismiss snoozes for 7 days.
//   - Signup suppresses indefinitely for this campaign.

const STORAGE_KEY = "loops_exit_popup_state";
const SESSION_KEY = "loops_exit_popup_shown_session";
const DISMISS_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const THROTTLE_MS = 24 * 60 * 60 * 1000;

function readState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeState(next) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable — just forget suppression this session
  }
}

function isSuppressed() {
  if (typeof window === "undefined") return true;
  try {
    if (window.sessionStorage.getItem(SESSION_KEY) === "1") return true;
  } catch {
    // sessionStorage unavailable — treat as not-shown-this-session
  }
  const now = Date.now();
  const state = readState();
  if (state.converted) return true;
  if (state.dismissedUntil && state.dismissedUntil > now) return true;
  if (state.lastShownAt && now - state.lastShownAt < THROTTLE_MS) return true;
  return false;
}

function markShown() {
  try {
    window.sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    // ignore
  }
  writeState({ ...readState(), lastShownAt: Date.now() });
}

function markDismissed() {
  writeState({ ...readState(), dismissedUntil: Date.now() + DISMISS_SNOOZE_MS });
}

function markConverted() {
  writeState({ ...readState(), converted: true });
}

// --- placement (exclude app-interior, conversion, and legal pages) ----------

const EXCLUDED_PATH_PATTERNS = [
  /^\/about(\/|$)/, // has inline form
  /^\/subscribe(\/|$)/, // in-progress conversion
  /^\/subscription-success(\/|$)/,
  /^\/confirmation(\/|$)/,
  /^\/login(\/|$)/,
  /^\/forgot-password(\/|$)/,
  /^\/reset-password(\/|$)/,
  /^\/sessions(\/|$)/,
  /^\/session-history(\/|$)/,
  /^\/session-end(\/|$)/,
  /^\/profile(\/|$)/,
  /^\/privacy-policy(\/|$)/,
  /^\/terms-of-service(\/|$)/,
  /^\/disclaimer(\/|$)/,
  /^\/return-policy(\/|$)/,
  /^\/emergency-support(\/|$)/,
  /^\/contact(\/|$)/,
];

function shouldShowOnPath(pathname) {
  return !EXCLUDED_PATH_PATTERNS.some((re) => re.test(pathname));
}

// --- escape hatches ---------------------------------------------------------

function hasNopopupOverride(search) {
  if (!search) return false;
  try {
    const params = new URLSearchParams(search);
    return params.get("nopopup") === "1";
  } catch {
    return false;
  }
}

// --- trigger detection -------------------------------------------------------
// Desktop: mouseleave-from-top after minDwellMs. Backup: time + scroll.
// Mobile:  sustained inactivity after minDwellMs once scroll >= scrollPct.

function useExitTrigger(enabled, config) {
  const {
    minDwellMs = 10000,
    backupTimeMs = 45000,
    backupScrollPct = 50,
    mobileInactivityMs = 5000,
    mobileMinScrollPct = 40,
    mobileMinDwellMs = 30000,
  } = config || {};

  const [trigger, setTrigger] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const startedAt = Date.now();
    const isMobile = window.matchMedia
      ? window.matchMedia("(max-width: 767px), (pointer: coarse)").matches
      : false;

    let inactivityTimer = null;
    let backupTimer = null;
    let maxScrollPct = 0;
    let fired = false;

    const fire = (reason) => {
      if (fired) return;
      fired = true;
      setTrigger(reason);
    };

    const computeScrollPct = () => {
      const docHeight = Math.max(
        document.documentElement.scrollHeight - window.innerHeight,
        1,
      );
      return Math.min(100, (window.scrollY / docHeight) * 100);
    };

    const resetInactivity = () => {
      if (!isMobile) return;
      if (inactivityTimer) clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        const dwellMs = Date.now() - startedAt;
        if (
          dwellMs >= mobileMinDwellMs &&
          maxScrollPct >= mobileMinScrollPct
        ) {
          fire("mobile_inactivity");
        }
      }, mobileInactivityMs);
    };

    const onScroll = () => {
      const pct = computeScrollPct();
      if (pct > maxScrollPct) maxScrollPct = pct;
      if (isMobile) resetInactivity();
    };

    const onMouseLeave = (e) => {
      if (isMobile) return;
      // mouse exited from the top of the viewport
      if (e.clientY <= 0) {
        if (Date.now() - startedAt >= minDwellMs) fire("mouseleave");
      }
    };

    backupTimer = setTimeout(() => {
      if (maxScrollPct >= backupScrollPct) fire("time_scroll");
    }, backupTimeMs);

    document.addEventListener("mouseleave", onMouseLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    if (isMobile) {
      window.addEventListener("touchstart", resetInactivity, { passive: true });
      resetInactivity();
    }

    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("scroll", onScroll);
      if (isMobile) {
        window.removeEventListener("touchstart", resetInactivity);
      }
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (backupTimer) clearTimeout(backupTimer);
    };
  }, [
    enabled,
    minDwellMs,
    backupTimeMs,
    backupScrollPct,
    mobileInactivityMs,
    mobileMinScrollPct,
    mobileMinDwellMs,
  ]);

  return trigger;
}

// --- component ---------------------------------------------------------------

function LoopsExitPopup() {
  const { pathname, search } = useLocation();
  const { isSubscribed } = useAuth();

  const isMobile = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(max-width: 767px), (pointer: coarse)").matches;
  }, []);

  const source = isMobile ? "exit-intent-mobile" : "exit-intent-desktop";

  // Re-evaluate suppression on every path change so an earlier trigger on a
  // different page doesn't leave a stale "enabled=false" in scope.
  const enabled = useMemo(() => {
    if (hasNopopupOverride(search)) return false;
    if (isSubscribed) return false;
    if (!shouldShowOnPath(pathname)) return false;
    if (isSuppressed()) return false;
    return true;
  }, [isSubscribed, pathname, search]);

  const trigger = useExitTrigger(enabled);

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const triggerReasonRef = useRef(null);

  const dismiss = useCallback(
    (reason = "close_button") => {
      setOpen(false);
      markDismissed();
      trackEvent("exit_popup_dismissed", {
        trigger_type: triggerReasonRef.current || "unknown",
        path: pathname,
        source,
        reason,
      });
      // Reset form state shortly after close so a re-open doesn't flash stale copy.
      setTimeout(() => {
        setEmail("");
        setStatus("idle");
        setErrorMessage("");
      }, 200);
    },
    [pathname, source],
  );

  useEffect(() => {
    if (!trigger) return;
    triggerReasonRef.current = trigger;
    setOpen(true);
    markShown();
    trackEvent("exit_popup_shown", {
      trigger_type: trigger,
      path: pathname,
      source,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") dismiss("escape");
    };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  // Lock background scroll while the modal is open (polish, avoids jank on mobile).
  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const { style } = document.body;
    const prevOverflow = style.overflow;
    style.overflow = "hidden";
    return () => {
      style.overflow = prevOverflow;
    };
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    setErrorMessage("");

    try {
      const body = new URLSearchParams();
      body.append("email", email);
      body.append("source", source);
      body.append("userGroup", LOOPS_DEFAULT_USER_GROUP);
      body.append("trigger_type", triggerReasonRef.current || "unknown");
      body.append("path", pathname);

      const response = await fetch(LOOPS_NEWSLETTER_FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (response.ok) {
        markConverted();
        setStatus("success");
        trackEvent("exit_popup_converted", {
          trigger_type: triggerReasonRef.current || "unknown",
          path: pathname,
          source,
        });
      } else {
        let msg = "Something went wrong. Please try again.";
        try {
          const data = await response.json();
          if (data?.message) msg = data.message;
        } catch {
          // ignore JSON parse errors
        }
        setErrorMessage(msg);
        setStatus("error");
        trackEvent("exit_popup_submit_error", {
          trigger_type: triggerReasonRef.current || "unknown",
          path: pathname,
          source,
          status: response.status,
        });
      }
    } catch {
      setErrorMessage("Network error. Please try again.");
      setStatus("error");
      trackEvent("exit_popup_submit_error", {
        trigger_type: triggerReasonRef.current || "unknown",
        path: pathname,
        source,
        status: "network",
      });
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => dismiss("backdrop")}
      role="dialog"
      aria-modal="true"
      aria-labelledby="loops-exit-title"
    >
      <div
        className="relative w-full max-w-md rounded-lg bg-background p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={() => dismiss("close_button")}
          className="absolute right-3 top-3 text-2xl leading-none text-textSecondary hover:text-text"
        >
          ×
        </button>

        {status === "success" ? (
          <div className="text-center">
            <h4
              id="loops-exit-title"
              className="mb-3 text-2xl font-normal text-secondary"
            >
              Thanks — the first email is on its way.
            </h4>
            <p className="text-sm text-textSecondary">
              Seven short emails, coming your way over the next two weeks.
            </p>
          </div>
        ) : (
          <>
            <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-accent">
              Before you go
            </p>
            <h4
              id="loops-exit-title"
              className="mb-2 text-2xl font-normal text-secondary"
            >
              Still figuring it out? Start with the email series.
            </h4>
            <p className="mb-4 text-sm text-textSecondary">
              Seven short emails on the research behind bilateral stimulation — what it is, whether it works, and how to know if it's right for you. No pressure, no pitch.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <label htmlFor="loops-exit-email" className="sr-only">
                Email
              </label>
              <input
                id="loops-exit-email"
                type="email"
                required
                aria-required="true"
                autoComplete="email"
                placeholder="Email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                autoFocus
                className="mb-4 w-full rounded border border-tint bg-white px-3 py-2.5 text-sm text-text focus:border-primary/30 focus:ring-2 focus:ring-primary/30 focus:outline-none"
              />

              {status === "error" && errorMessage && (
                <p className="mb-3 text-xs text-red-600">{errorMessage}</p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full rounded bg-accent hover:bg-accentDark py-2.5 text-sm font-bold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === "loading" ? "Sending..." : "Send me the series"}
              </button>

              <button
                type="button"
                onClick={() => dismiss("no_thanks")}
                className="mt-3 block w-full text-center text-xs text-textSecondary underline hover:text-textSecondary"
              >
                No thanks, I'll skip the emails
              </button>

              <p className="mt-2 text-center text-xs text-textSecondary">
                Unsubscribe anytime.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default LoopsExitPopup;
